"use client"

import { motion, useMotionValue, useReducedMotion, useSpring, type HTMLMotionProps } from "framer-motion"
import { createContext, useContext, useId, useState, type PointerEvent, type ReactNode } from "react"

import { cn } from "@/lib/utils"
import { Plus } from "lucide-react"

type TactileLinkProps = Omit<HTMLMotionProps<"a">, "onPointerMove" | "onPointerLeave" | "onAnimationStart" | "style"> & {
  children: ReactNode
  magnetic?: boolean
}

export function TactileLink({ children, className, magnetic = true, ...props }: TactileLinkProps) {
  const reduceMotion = useReducedMotion()
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const springX = useSpring(x, { stiffness: 260, damping: 24, mass: 0.45 })
  const springY = useSpring(y, { stiffness: 260, damping: 24, mass: 0.45 })

  const handlePointerMove = (event: PointerEvent<HTMLAnchorElement>) => {
    if (reduceMotion || !magnetic || event.pointerType !== "mouse") return

    const bounds = event.currentTarget.getBoundingClientRect()
    const offsetX = (event.clientX - (bounds.left + bounds.width / 2)) / (bounds.width / 2)
    const offsetY = (event.clientY - (bounds.top + bounds.height / 2)) / (bounds.height / 2)
    x.set(offsetX * 6)
    y.set(offsetY * 4)
  }

  const resetPosition = () => {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.a
      {...props}
      className={className}
      style={{ x: springX, y: springY }}
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPosition}
      whileTap={reduceMotion ? undefined : { scale: 0.96 }}
    >
      {children}
    </motion.a>
  )
}

type TactileMediaProps = {
  children: ReactNode
  className?: string
  strength?: number
}

export function TactileMedia({ children, className, strength = 4 }: TactileMediaProps) {
  const reduceMotion = useReducedMotion()
  const rotateX = useMotionValue(0)
  const rotateY = useMotionValue(0)
  const springRotateX = useSpring(rotateX, { stiffness: 220, damping: 26, mass: 0.6 })
  const springRotateY = useSpring(rotateY, { stiffness: 220, damping: 26, mass: 0.6 })

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (reduceMotion || event.pointerType !== "mouse") return

    const bounds = event.currentTarget.getBoundingClientRect()
    const offsetX = (event.clientX - bounds.left) / bounds.width - 0.5
    const offsetY = (event.clientY - bounds.top) / bounds.height - 0.5
    rotateX.set(-offsetY * strength)
    rotateY.set(offsetX * strength)
  }

  const resetTilt = () => {
    rotateX.set(0)
    rotateY.set(0)
  }

  return (
    <div className={cn("relative [perspective:1400px]", className)} onPointerMove={handlePointerMove} onPointerLeave={resetTilt}>
      <motion.div className="w-full [transform-style:preserve-3d]" style={{ rotateX: springRotateX, rotateY: springRotateY }}>
        {children}
      </motion.div>
    </div>
  )
}

type TactileDisclosureProps = {
  children: ReactNode
  label: ReactNode
  defaultOpen?: boolean
  groupId?: string
  className?: string
  buttonClassName?: string
  contentClassName?: string
}

type TactileDisclosureGroupContextValue = {
  openId: string | null
  setOpenId: (id: string | null) => void
}

const TactileDisclosureGroupContext = createContext<TactileDisclosureGroupContextValue | null>(null)

type TactileDisclosureGroupProps = {
  children: ReactNode
  defaultOpenId?: string | null
}

export function TactileDisclosureGroup({ children, defaultOpenId = null }: TactileDisclosureGroupProps) {
  const [openId, setOpenId] = useState<string | null>(defaultOpenId)

  return <TactileDisclosureGroupContext.Provider value={{ openId, setOpenId }}>{children}</TactileDisclosureGroupContext.Provider>
}

export function TactileDisclosure({ children, label, defaultOpen = false, groupId, className, buttonClassName, contentClassName }: TactileDisclosureProps) {
  const contentId = useId()
  const [open, setOpen] = useState(defaultOpen)
  const group = useContext(TactileDisclosureGroupContext)
  const disclosureId = groupId ?? contentId
  const isOpen = group ? group.openId === disclosureId : open

  const handleToggle = () => {
    if (group) {
      group.setOpenId(group.openId === disclosureId ? null : disclosureId)
      return
    }

    setOpen((current) => !current)
  }

  return (
    <div className={cn("tactile-disclosure", isOpen && "tactile-disclosure-open", className)}>
      <button
        type="button"
        className={cn("flex w-full items-center justify-between gap-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-card", buttonClassName)}
        aria-controls={contentId}
        aria-expanded={isOpen}
        onClick={handleToggle}
      >
        <span className="min-w-0 flex-1">{label}</span>
        <span className={cn("tactile-disclosure-toggle flex size-9 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-secondary/70 text-primary transition duration-300", isOpen && "rotate-45 bg-primary text-primary-foreground")} aria-hidden="true">
          <Plus className="size-4" strokeWidth={2} />
        </span>
      </button>
      <div id={contentId} className={cn("tactile-disclosure-panel", isOpen && "tactile-disclosure-panel-open", contentClassName)} aria-hidden={!isOpen}>
        <div className="tactile-disclosure-inner">{children}</div>
      </div>
    </div>
  )
}
